import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { updateEdiEvent } from '../../services/edievents.service';

/**
 * Bespoke edit drawer for EDI Events (cms.edi_events / edi_events_archive). The
 * event data (EDI Handle / Pro Number / Event / Event Date) is always read-only —
 * the page only lets users mark an event sent/unsent (Sent Date) or deleted. Those
 * two are editable ONLY for MAIN rows (source='main'); ARCHIVE rows (source='archive')
 * are fully locked (read-only, no save). Replaces V1 editedi_events / editedi_events_archive.
 */
const TABLE = 'edi-events';
const s = (v: any): string => (v == null ? '' : String(v));

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {(children == null || children === '') ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

const EdiEventsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const recno = row ? Number(row.recno) : 0;
  const isArchive = s(row?.source) === 'archive';

  // datetime-local wants "YYYY-MM-DDTHH:MM"; the row's sentdatetime is "YYYY-MM-DD HH:MM".
  const [sent, setSent] = useState(s(row?.sentdatetime).replace(' ', 'T'));
  const [formDeleted, setFormDeleted] = useState(s(row?.deleted) === 'Y');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const dirty = useMemo(
    () => sent !== s(row?.sentdatetime).replace(' ', 'T') || formDeleted !== (s(row?.deleted) === 'Y'),
    [sent, formDeleted, row],
  );

  const handleSave = useCallback(async () => {
    if (isArchive) return;
    if (!dirty) { onClose(); return; }
    setSaveError(null);
    setIsSaving(true);
    try {
      await updateEdiEvent(recno, { sentdatetime: sent, deleted: formDeleted ? 'Y' : 'N' });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to update EDI event.');
      setIsSaving(false);
    }
  }, [isArchive, dirty, recno, sent, formDeleted, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        {isArchive ? 'Close' : 'Cancel'}
      </button>
      {!isArchive && (
        <button onClick={handleSave} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
            : (<><CheckIcon className="w-4 h-4" /> Save Changes</>)}
        </button>
      )}
    </div>
  );

  const title = `EDI Event — Pro #${s(row?.pronumber)}`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="44rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!isArchive && !!recno && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} />
          </div>
        )}

        {isArchive && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-3 flex items-start gap-2">
            <LockClosedIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This is an <b>archived</b> event — read only. The Sent Date and Deleted status can't be changed here.
            </p>
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}

        {/* Event data — always read-only. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="EDI Handle">{s(row?.edihandle)}</Field>
          <Field label="Pro Number">{s(row?.pronumber)}</Field>
          <Field label="Event">{s(row?.event)}</Field>
          <Field label="Event Date">{s(row?.eventdatetime)}</Field>
        </div>

        {/* Sent Date + Deleted — editable for main rows, read-only for archive. */}
        {isArchive ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Sent Date">{s(row?.sentdatetime)}</Field>
            <Field label="Deleted">{s(row?.deleted) === 'Y' ? 'Yes' : 'No'}</Field>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-label mb-1">Sent Date</label>
              <div className="flex items-center gap-2">
                <input type="datetime-local" value={sent} disabled={isSaving}
                  onChange={(e) => setSent(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                {sent && (
                  <button type="button" onClick={() => setSent('')} disabled={isSaving}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                    Mark unsent
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-muted">
                Set a Sent Date to mark the event sent, or clear it to mark it unsent.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <label className="flex items-start gap-3 cursor-pointer select-none py-1">
                <input type="checkbox" checked={formDeleted} disabled={isSaving}
                  onChange={(e) => setFormDeleted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm font-medium text-label">Deleted</span>
              </label>
              <p className="mt-1 ml-7 text-xs text-muted">
                Marks this event deleted — hidden from filters but preserved in history. Uncheck and save to restore.
              </p>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default EdiEventsEditDrawer;
